import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Eye, Heart, Gauge, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmt } from "@/components/publish/PubStats";
import { YT_ON_HOLD, YtBuildBadge } from "@/components/publish/YtBuild";

const TONE = { hype: "#ff5a36", funny: "#ffcc4d", cinematic: "#3ad7ff" };

export const StyleTable = () => {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/stats/styles").then((r) => setD(r.data)).catch(() => setD({ styles: [], best: null }));
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    const ok = await api.post("/publications/refresh").then(() => true).catch(() => false);
    await load();
    setBusy(false);
    if (ok) toast.success(t("pub.refreshed")); else toast.error(t("pub.refresh_failed"));
  };
  const max = Math.max(1, ...(d?.styles || []).map((s) => s.avg_views));
  return (
    <div className="cx-card rounded-2xl mt-6 overflow-hidden" data-testid="style-table">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-white/[0.05]">
        <Trophy className="w-4 h-4 text-[#ffcc4d]" />
        <span className="text-xs uppercase tracking-wider text-slate-400 flex-1">{t("sty.title")}</span>
        {YT_ON_HOLD && <YtBuildBadge testid="style-yt-build-badge" />}
        <span className="hidden md:inline text-[10px] text-slate-500">{t("sty.legend")}</span>
        <button onClick={refresh} disabled={busy || YT_ON_HOLD} data-testid="style-refresh-btn" className="cx-ghost rounded-full px-3 py-1.5 text-[11px] flex items-center gap-1.5">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />{t("pub.refresh")}
        </button>
      </div>
      {d?.styles.map((s) => {
        const color = TONE[s.style];
        const best = d.best === s.style;
        return (
          <div key={s.style} className="grid grid-cols-[90px_minmax(0,1fr)_auto_auto_auto] md:grid-cols-[130px_minmax(0,1fr)_90px_90px_90px] gap-x-3 gap-y-1 items-center px-4 py-3 border-t border-white/[0.04]" data-testid={`style-row-${s.style}`}>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{t(`var.${s.style}`)}</span>
              {best && <Trophy className="w-3.5 h-3.5 text-[#ffcc4d]" data-testid="style-best" />}
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${s.avg_views ? Math.max(4, (s.avg_views / max) * 100) : 0}%`, background: color }} />
            </div>
            <div className="text-xs font-mono text-white flex items-center gap-1" title={t("sty.avg_views")} data-testid={`style-views-${s.style}`}><Eye className="w-3 h-3 text-[#3ad7ff]" />{fmt(s.avg_views)}</div>
            <div className="text-xs font-mono text-white flex items-center gap-1" title={t("sty.likes")}><Heart className="w-3 h-3 text-[#ff5a8a]" />{fmt(s.likes)}</div>
            <div className="text-xs font-mono text-[#c79bff] flex items-center gap-1" title={t("sty.avg_score")} data-testid={`style-score-${s.style}`}><Gauge className="w-3 h-3" />{s.avg_score}</div>
            <div className="col-span-5 text-[10px] text-slate-500">{t("sty.counts", { c: s.clips, p: s.published })}</div>
          </div>
        );
      })}
      {d && !d.best && <p className="px-4 py-3 text-[11px] text-slate-500 border-t border-white/[0.04]" data-testid="style-empty">{t("sty.empty")}</p>}
    </div>
  );
};
